import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/lib/supabase";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import type { LicenseCode } from "@/pages/admin/tools/hooks/useLicenseTools";
import type { AdminLicense } from "@/pages/admin/hooks/useAdminData";
import type { WebhookDelivery, WebhookEndpoint } from "@/pages/admin/approvals/hooks/useWebhooks";
import type { HealthWindow } from "@/lib/webhookHealth";
import type { ReportContext, ReportTranslate, RunnableReport } from "@/lib/reportCatalog";

export interface ExportTask {
  id: number;
  report_key: string;
  report_name: string;
  category: string | null;
  row_count: number;
  // 带时间窗口的报表（如端点健康快照）记录所用的窗口，导出历史里直观展示。
  window_label: string | null;
  exported_by: string | null;
  exported_by_name: string | null;
  created_at: string;
}

async function invokeAdmin<T>(payload: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("admin-license", { body: payload });
  if (error) {
    let message = error.message || "操作失败，请稍后重试";
    try {
      const context = (error as { context?: Response }).context;
      if (context) {
        const parsed = await context.json();
        if (parsed?.error) message = parsed.error;
      }
    } catch {
      // keep original message
    }
    throw new Error(message);
  }
  const result = data as { error?: string } | null;
  if (result?.error) throw new Error(result.error);
  return data as T;
}

// 回调端点数据走独立的 admin-webhooks 函数，供「回调端点健康快照」这类报表直接生成。
async function invokeWebhooks<T>(payload: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("admin-webhooks", { body: payload });
  if (error) throw new Error(error.message || "回调数据加载失败");
  const result = data as { error?: string } | null;
  if (result?.error) throw new Error(result.error);
  return data as T;
}

interface UseReportCenterResult {
  codes: LicenseCode[];
  licenses: AdminLicense[];
  tasks: ExportTask[];
  context: ReportContext;
  loading: boolean;
  error: string | null;
  healthWindow: HealthWindow;
  setHealthWindow: (window: HealthWindow) => void;
  reload: () => void;
  runReport: (report: RunnableReport) => Promise<number>;
  runBatchReports: (reports: RunnableReport[]) => Promise<{ count: number; total: number }>;
}

export function useReportCenter(): UseReportCenterResult {
  const { user, loading: authLoading } = useCurrentUser();
  const { t } = useTranslation();
  const [codes, setCodes] = useState<LicenseCode[]>([]);
  const [licenses, setLicenses] = useState<AdminLicense[]>([]);
  const [tasks, setTasks] = useState<ExportTask[]>([]);
  const [webhookEndpoints, setWebhookEndpoints] = useState<WebhookEndpoint[]>([]);
  const [webhookDeliveries, setWebhookDeliveries] = useState<WebhookDelivery[]>([]);
  // 回调端点健康快照的时间窗口：单份生成与批量导出共用同一个口径。
  const [healthWindow, setHealthWindow] = useState<HealthWindow>("7d");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) {
      setCodes([]);
      setLicenses([]);
      setTasks([]);
      setWebhookEndpoints([]);
      setWebhookDeliveries([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [codesData, adminData, taskData, webhookData] = await Promise.all([
        invokeAdmin<{ codes: LicenseCode[] }>({ action: "listCodes" }),
        invokeAdmin<{ licenses: AdminLicense[] }>({ action: "list" }),
        invokeAdmin<{ tasks: ExportTask[] }>({ action: "listReportExports" }),
        // 回调端点数据缺失不应阻断其它报表，单独降级为无数据。
        invokeWebhooks<{ endpoints?: WebhookEndpoint[]; deliveries?: WebhookDelivery[] }>({ action: "list" }).catch(
          () => ({ endpoints: [], deliveries: [] }),
        ),
      ]);
      setCodes(codesData.codes ?? []);
      setLicenses(adminData.licenses ?? []);
      setTasks(taskData.tasks ?? []);
      setWebhookEndpoints(webhookData.endpoints ?? []);
      setWebhookDeliveries(webhookData.deliveries ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "报表中心数据加载失败");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  const reloadTasks = useCallback(async () => {
    try {
      const taskData = await invokeAdmin<{ tasks: ExportTask[] }>({ action: "listReportExports" });
      setTasks(taskData.tasks ?? []);
    } catch {
      // 历史刷新失败不影响本次导出结果
    }
  }, []);

  const context = useMemo<ReportContext>(
    () => ({
      codes,
      licenses,
      webhookEndpoints,
      webhookDeliveries,
      healthWindow,
      translate: t as unknown as ReportTranslate,
    }),
    [codes, licenses, webhookEndpoints, webhookDeliveries, healthWindow, t],
  );

  // 健康快照导出的留痕名称带上所选时间窗口，便于在导出历史里区分不同窗口的快照。
  const healthWindowLabel = t(`approvals.healthWindow${healthWindow}`);

  // 生成报表 → 写入导出任务历史 → 刷新历史列表，全程留痕可追溯。
  const runReport = useCallback(
    async (report: RunnableReport) => {
      const count = await report.run(context);
      try {
        await invokeAdmin<{ ok: boolean }>({
          action: "logReportExport",
          reportKey: report.key,
          reportName:
            report.key === "webhook-health" ? `${report.name} · ${healthWindowLabel}` : report.name,
          category: report.category,
          rowCount: count,
          windowLabel: report.key === "webhook-health" ? healthWindowLabel : undefined,
        });
        await reloadTasks();
      } catch {
        // 留痕写入失败不回滚已完成的导出
      }
      return count;
    },
    [context, reloadTasks, healthWindowLabel],
  );

  // 批量导出：勾选的多份报表依次生成，最后汇总为「一条」导出任务记录，
  // 让多次导出在历史里以一次批量操作的形式归档，便于月度审计。
  const runBatchReports = useCallback(
    async (reports: RunnableReport[]) => {
      let total = 0;
      for (const report of reports) {
        // 逐份生成，保证已有文件先落盘，再写汇总留痕
        const count = await report.run(context);
        total += count;
      }
      try {
        const includesHealth = reports.some((report) => report.key === "webhook-health");
        await invokeAdmin<{ ok: boolean }>({
          action: "logReportExport",
          reportKey: `batch:${reports.map((report) => report.key).join("+").slice(0, 74)}`,
          reportName: includesHealth
            ? `批量导出 · ${reports.length} 份报表 · 健康快照 ${healthWindowLabel}`
            : `批量导出 · ${reports.length} 份报表`,
          category: "批量",
          rowCount: total,
          windowLabel: includesHealth ? healthWindowLabel : undefined,
        });
        await reloadTasks();
      } catch {
        // 留痕写入失败不回滚已完成的导出
      }
      return { count: reports.length, total };
    },
    [context, reloadTasks, healthWindowLabel],
  );

  return {
    codes,
    licenses,
    tasks,
    context,
    loading: loading || authLoading,
    error,
    healthWindow,
    setHealthWindow,
    reload: load,
    runReport,
    runBatchReports,
  };
}